'use client';

import styles from './search-bar.module.scss';
import SearchIcon from 'assets/icons/search.svg';
import LoadingIcon from 'assets/icons/loading-spinner.svg';
import { DebounceInput } from 'react-debounce-input';
import {
	type ChangeEventHandler,
	type KeyboardEventHandler,
	type MouseEventHandler,
	type ReactNode,
	type RefObject,
	useId,
} from 'react';
import { clearFocus } from 'helpers/clear-focus';

/** @deprecated in favor of SearchBarCombobox */
const SearchBar = ({
	onChange = () => {},
	name,
	onKeyDown,
	onButtonClick,
	debounceTimeout = 200,
	initialQuery,
	placeholderText = 'Search',
	isSearching,
	icon,
	numericOnly = false,
	id,
	list,
	inputRef,
}: Props) => {
	const classString = isSearching
		? `${styles.searching} ${styles.wrapper}`
		: styles.wrapper;
	const listID = useId();

	const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (onKeyDown) {
			return onKeyDown(e);
		}

		if (e.key === 'Enter') {
			clearFocus();
		}
	};

	const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		if (onButtonClick) {
			return onButtonClick(e);
		}

		clearFocus();
	};

	return (
		<div className={classString}>
			{!!icon && <div className={styles.iconContainer}>{icon}</div>}
			<DebounceInput
				name={name}
				id={id}
				inputRef={inputRef}
				debounceTimeout={debounceTimeout}
				className={styles.input}
				placeholder={placeholderText}
				aria-label={placeholderText}
				onKeyDown={handleKeyDown}
				type={numericOnly ? 'numeric' : 'search'}
				onChange={onChange}
				value={initialQuery}
				inputMode={numericOnly ? 'numeric' : 'search'}
				pattern={numericOnly ? '[0-9]*' : '.*'}
				list={listID}
			/>
			<button
				aria-label="Search Submit"
				className={styles.submitButton}
				disabled={isSearching} // besides preventing double clicks, this is a helpful selector for e2e tests
				onClick={handleButtonClick}
			>
				{isSearching ? <LoadingIcon /> : <SearchIcon />}
			</button>

			{list?.length > 0 && (
				<datalist id={listID}>
					{list.map((accessionNumber) => {
						return <option key={accessionNumber} value={accessionNumber} />;
					})}
				</datalist>
			)}
		</div>
	);
};

interface Props {
	name?: string;
	id?: string;
	isSearching?: boolean;
	onChange?: ChangeEventHandler<HTMLInputElement>;
	onButtonClick?: MouseEventHandler<HTMLButtonElement>;
	onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	debounceTimeout?: number;
	initialQuery?: string;
	placeholderText?: string;
	numericOnly?: boolean;
	icon?: ReactNode;
	list?: string[];
	inputRef?: RefObject<HTMLInputElement | null>;
}

export default SearchBar;
